import React, { useContext, useState, useEffect } from "react";
import moment from "moment";
import styled, { ThemeContext } from "styled-components";
import LineChart from "./LineChart";
import BarChart from "./RoundedBarsChart";
import { Button } from "components/shared";
import { Dropdown } from "semantic-ui-react";
import { sortByKey } from "utils/shared";
import { findErrorIntervals, historyValue } from "./errorBands";

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledDropdown = styled(Dropdown)``;

const LineControlsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const DropdownWrapper = styled.div`
  display: flex;
`;

const DropdownIndicator = styled.div`
  height: 14px;
  width: 14px;
  border-radius: 7px;
  background-color: ${(props) => props.color};
  margin-right: 8px;
  margin-bottom: 1px;
`;

const ResetZoomButton = styled(Button)`
  &&& {
    background-color: transparent;
    color: ${(props) => props.theme.textDark};
    border: 2px solid ${(props) => props.theme.accentColor};
    border-radius: 5px;
    box-shadow: 0;
  }
`;

// Monthly view header (2026-10-05 request, modelled on a reference SCADA
// screenshot): Produced / Average power / Average wind for the year shown.
const SummaryWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 2rem;
  margin-bottom: 1rem;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${(props) => props.theme.textDark};
`;

const SummaryValue = styled.span`
  font-size: 1.2rem;
  font-weight: 700;
`;

const SummaryLabel = styled.span`
  font-size: 0.85rem;
  color: ${(props) => props.theme.textMedium};
`;

const formatNumber = (value, digits) =>
  new Intl.NumberFormat("en-GB", { maximumFractionDigits: digits }).format(value);

const formatEnergy = (kwh) =>
  Math.abs(kwh) >= 10000 ? `${formatNumber(kwh / 1000, 2)} MWh` : `${formatNumber(kwh, 0)} kWh`;

const CHART_TIME_UNITS = {
  Hourly: "hour",
  Daily: "day",
  Monthly: "month",
};

const config = (
  theme,
  chartType,
  dataset1,
  dataset2,
  setIsZooming,
  isZooming,
  firstLineItem,
  secondLineItem,
  firstLineParam,
  secondLineParam,
  turbineParams,
  errorIntervals
) => {
  // In the Daily/Monthly bar charts the server sends KWH_METER as the energy
  // produced in that period (not the running meter), so it reads as
  // "Production" and its bars must start at zero.
  const isProductionBars = (param) =>
    chartType !== "Hourly" &&
    param &&
    param.dbname &&
    param.dbname.toLowerCase() === "kwh_meter";
  const firstIsProduction = isProductionBars(firstLineParam);
  const secondIsProduction = isProductionBars(secondLineParam);

  return {
    data: {
      labels: dataset1.labels,
      datasets: [
        {
          backgroundcolor: theme.graphAxle1,
          // Chart.js reads backgroundColor (the lowercase one above never
          // did anything) - without it the Daily/Monthly bars had no fill.
          backgroundColor: theme.graphAxle1,
          borderColor: theme.graphAxle1,
          data: dataset1.data,
          label: firstIsProduction ? "Production" : firstLineItem,
          unit: firstLineParam ? firstLineParam.unit : "",
          details:
            firstLineParam.details ||
            (firstLineParam.name.toLowerCase() === "error"
              ? turbineParams.Failure
              : null),
          fill: false,
          yAxisID: "y-axis-1",
          pointRadius: 0,
          borderWidth: 2,
          spanGaps: false,
          // Chart.js v2 defaults to a 0.4 bezier tension, which overshoots
          // below/above the real min/max between two points on noisy data -
          // looks like it "goes negative" even though no value in the
          // dataset actually is. Straight lines instead.
          lineTension: 0,
        },
        {
          backgroundcolor: theme.graphAxle2,
          backgroundColor: theme.graphAxle2,
          borderColor: theme.graphAxle2,
          data: dataset2.data,
          label: secondIsProduction ? "Production" : secondLineItem,
          unit: secondLineParam ? secondLineParam.unit : "",
          details:
            secondLineParam.details ||
            (secondLineParam.name.toLowerCase() === "error"
              ? turbineParams.Failure
              : null),
          fill: false,
          yAxisID: "y-axis-2",
          pointRadius: 0,
          borderWidth: 2,
          spanGaps: false,
          lineTension: 0,
        },
      ],
    },
    options: {
      animation: {
        duration: 0, // general animation time
      },
      legend: {
        display: false,
      },
      tooltips: {
        mode: "index",
        intersect: false,
        backgroundcolor: "rgba(0, 0, 0, 0.5)",
        bodySpacing: 4,
        callbacks: {
          label: function (tooltipItem, data) {
            var label = data.datasets[tooltipItem.datasetIndex].label || "";
            if (label) {
              label += ": ";
            }
            let value = tooltipItem.yLabel;
            const details = data.datasets[tooltipItem.datasetIndex].details;
            // A code missing from the type's Failure list used to throw here
            // and kill the whole tooltip.
            const failure = details && details.find((d) => d.number === value);
            if (failure) {
              value = `${failure.text} (${value})`;
            }
            label += `${value} ${
              data.datasets[tooltipItem.datasetIndex].unit || ""
            }`;
            return label;
          },
        },
      },
      scales: {
        xAxes: [
          chartType === "Monthly"
            ? {
                // Always Jan..Dec of the year shown, with empty months as
                // empty slots, and the two series as side-by-side bars per
                // month - a time axis only spanned the months that had
                // data and didn't group the two datasets per month.
                type: "category",
                offset: true,
                gridLines: { display: false },
                ticks: { fontColor: theme.textMedium },
              }
            : {
                type: "time",
                offset: true,
                bounds: "ticks",
                time: {
                  unit: CHART_TIME_UNITS[chartType],
                  stepSize: 1,
                  displayFormats: {
                    hour: "HH:mm",
                  },
                },
                gridLines: {
                  display: false,
                },
                ticks: {
                  fontColor: theme.textMedium,
                },
              },
        ],
        yAxes: [
          {
            id: "y-axis-1",
            position: "left",
            gridLines: {
              drawBorder: false,
              color: theme.controlsBackgroundColor,
              zeroLineColor: theme.controlsBackgroundColor,
            },
            ticks: {
              fontColor: theme.textMedium,
              beginAtZero:
                firstLineItem === "Production" && !firstIsProduction
                  ? false
                  : true,
              // precision: 0,
            },
          },
          {
            id: "y-axis-2",
            position: "right",
            gridLines: {
              drawBorder: false,
              color: theme.controlsBackgroundColor,
              zeroLineColor: theme.controlsBackgroundColor,
            },
            ticks: {
              fontColor: theme.textMedium,
              beginAtZero:
                secondLineItem === "Production" && !secondIsProduction
                  ? false
                  : true,
              // precision: 0,
            },
          },
        ],
      },
      plugins: {
        streaming: false,
        errorBands: {
          intervals: errorIntervals || [],
          fillColor: "rgba(230, 70, 70, 0.18)",
          lineColor: "rgba(230, 70, 70, 0.7)",
          textColor: theme.textDark,
        },
        zoom: {
          zoom: {
            enabled: true,
            drag: true,
            mode: "x",
            onZoomComplete: () => setIsZooming(true),
          },
        },
      },
    },
  };
};

// Year shown in the Monthly view: the History page passes `date` already
// formatted as "YYYY" for it; falls back to the data's own year.
const monthlyYear = (date, data) => {
  const fromDate = parseInt(date, 10);
  if (Number.isFinite(fromDate) && fromDate > 1900) return fromDate;
  const row = (data || []).find((d) => Number.isFinite(d.year) && d.year >= 2020);
  return row ? row.year : moment().year();
};

const DataGraph = ({
  height = 70,
  chartType,
  date,
  data,
  detailedData,
  monitoringParams,
  turbineParams,
  schemaId,
  firstMonitoredParameter,
  secondMonitoredParameter,
  onfirstParameterChange,
  onsecondParameterChange,
}) => {
  const themeContext = useContext(ThemeContext);

  const [isZooming, setIsZooming] = useState(false);
  const [cfg, setCfg] = useState();
  //display chart only when params are defined

  const [showChart, setShowChart] = useState(
    typeof firstMonitoredParameter != "undefined" &&
      typeof secondMonitoredParameter != "undefined"
      ? true
      : false
  );

  useEffect(() => {
    if (showChart) {
      const getDatasetData = (datasetIndex) => {
        let dsLabels = [];
        let dsData = [];
        const first = monitoringParams.find(
          (p) => p.name === firstMonitoredParameter.name
        );
        const second = monitoringParams.find(
          (p) => p.name === secondMonitoredParameter.name
        );
        onfirstParameterChange({}, first);
        onsecondParameterChange({}, second);
        const key = datasetIndex === 0 ? first.dbname : second.dbname;
        const scale = datasetIndex === 0 ? first.scale || 1 : second.scale || 1;

        if (chartType === "Monthly") {
          const year = monthlyYear(date, data);
          const byMonth = {};
          (data || []).forEach((d) => {
            const m = Number.isFinite(d.month)
              ? { year: d.year, month: d.month }
              : { year: moment(d.timestamp).year(), month: moment(d.timestamp).month() };
            if (m.year === year) byMonth[m.month] = d;
          });
          for (let month = 0; month < 12; month++) {
            dsLabels.push(moment({ year, month, day: 1 }).format("MMM YYYY"));
            const row = byMonth[month];
            const raw = row && row.values ? parseFloat(row.values[key.toUpperCase()]) : NaN;
            dsData.push(Number.isFinite(raw) ? raw / scale : null);
          }
          return {
            labels: dsLabels,
            data: dsData,
            unit: datasetIndex === 0 ? first.unit : second.unit,
          };
        }

        const graphData =
          detailedData && detailedData.length > 0 ? detailedData : data; //isZooming &&
        graphData.forEach((d) => {
          if (!d.timestamp) {
            return;
          }

          const label = moment(Math.floor(d.timestamp));

          dsData.push(historyValue(d.values, key.toUpperCase(), schemaId) / scale);
          dsLabels.push(label);
        });

        return {
          labels: dsLabels,
          data: dsData,
          unit: datasetIndex === 0 ? first.unit : second.unit,
        };
      };

      if (data) {
        const dataset1 = getDatasetData(0);
        const dataset2 = getDatasetData(1);
        const errorIntervals =
          chartType === "Hourly"
            ? findErrorIntervals(
                detailedData && detailedData.length > 0 ? detailedData : data,
                {
                  failures: turbineParams && turbineParams.Failure,
                  states: turbineParams && turbineParams.State,
                  schemaId,
                }
              )
            : [];
        const conf = config(
          themeContext,
          chartType,
          dataset1,
          dataset2,
          setIsZooming,
          isZooming,
          firstMonitoredParameter.name,
          secondMonitoredParameter.name,
          firstMonitoredParameter,
          secondMonitoredParameter,
          turbineParams,
          errorIntervals
        );
        setCfg(conf);
      }
    }
    // eslint-disable-next-line
  }, [
    chartType,
    data,
    detailedData,
    firstMonitoredParameter,
    isZooming,
    secondMonitoredParameter,
    onfirstParameterChange,
    onsecondParameterChange,
  ]);

  if (!cfg) return null;

  const unsortedOption = monitoringParams.filter(
    (m) =>
      m.name !== firstMonitoredParameter.name &&
      m.name !== secondMonitoredParameter.name
  );
  const validOptions = sortByKey(unsortedOption, "name");
  const handleResetZoom = () => setIsZooming(false);

  // Produced = sum of the monthly production bars (KWH_METER per month,
  // computed server-side from the counter); Average power / wind = mean of
  // the months' averages, over the months that have one.
  const renderMonthlySummary = () => {
    const paramBy = (dbname) =>
      monitoringParams.find((p) => p.dbname && p.dbname.toLowerCase() === dbname);
    const year = monthlyYear(date, data);
    const rows = (data || []).filter((d) => d.values && (!Number.isFinite(d.year) || d.year === year));
    const valuesOf = (dbname) => {
      const param = paramBy(dbname);
      const scale = (param && param.scale) || 1;
      return rows
        .map((d) => parseFloat(d.values[dbname.toUpperCase()]) / scale)
        .filter((v) => Number.isFinite(v));
    };
    const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
    const produced = valuesOf("kwh_meter");
    const power = mean(valuesOf("power"));
    const wind = mean(valuesOf("wind"));
    const items = [
      produced.length ? [formatEnergy(produced.reduce((a, b) => a + b, 0)), "Produced"] : null,
      power !== null ? [`${formatNumber(power, 1)} kW`, "Average power"] : null,
      wind !== null ? [`${formatNumber(wind, 1)} m/s`, "Average wind"] : null,
    ].filter(Boolean);
    if (!items.length) return null;
    return (
      <SummaryWrapper>
        {items.map(([value, label]) => (
          <SummaryItem key={label}>
            <SummaryValue>{value}</SummaryValue>
            <SummaryLabel>{label}</SummaryLabel>
          </SummaryItem>
        ))}
      </SummaryWrapper>
    );
  };

  if (showChart) {
    return (
      <ChartWrapper>
        {chartType === "Monthly" && renderMonthlySummary()}
        {chartType === "Hourly" ? (
          <LineChart
            data={cfg.data}
            options={cfg.options}
            isZooming={isZooming}
          />
        ) : (
          <BarChart
            data={cfg.data}
            options={cfg.options}
            isZooming={isZooming}
          />
        )}
        <LineControlsWrapper>
          <DropdownWrapper>
            <DropdownIndicator color={themeContext.graphAxle1} />
            <StyledDropdown
              text={firstMonitoredParameter.name}
              direction="right"
              style={{ color: themeContext.textDark }}
              scrolling
            >
              <Dropdown.Menu>
                {validOptions.map((i) => (
                  <Dropdown.Item
                    key={i.name}
                    text={i.name}
                    unit={i.unit}
                    onClick={onfirstParameterChange}
                  />
                ))}
              </Dropdown.Menu>
            </StyledDropdown>
          </DropdownWrapper>

          {isZooming && (
            <ResetZoomButton onClick={handleResetZoom} secondary>
              Reset Zoom
            </ResetZoomButton>
          )}

          <DropdownWrapper>
            <DropdownIndicator color={themeContext.graphAxle2} />
            <StyledDropdown
              direction="left"
              text={secondMonitoredParameter.name}
              style={{ color: themeContext.textDark }}
              scrolling
            >
              <Dropdown.Menu>
                {validOptions.map((i) => (
                  <Dropdown.Item
                    key={`right:${i.name}`}
                    text={i.name}
                    unit={i.unit}
                    onClick={onsecondParameterChange}
                  />
                ))}
              </Dropdown.Menu>
            </StyledDropdown>
          </DropdownWrapper>
        </LineControlsWrapper>
      </ChartWrapper>
    );
  } else {
    return <ChartWrapper></ChartWrapper>;
  }
};

DataGraph.propTypes = {};

DataGraph.defaultProps = {};

export default React.memo(DataGraph);
